import { useEffect, useState } from "react";
import { Breadcrumbs } from "../../../components/breadcrumbs/index.jsx";
import { Loader } from "../../../components/loader/index.js";
import { Unavailable } from "../../../components/noData/index.js";

export const LiveUsers = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [emptyMessage, setEmptyMessage] = useState("");
  const humanDateTime = (value) => {
    if (!value) {
      return "-";
    }

    const normalizedValue = String(value).includes("T")
      ? String(value)
      : String(value).replace(" ", "T");
    const parsedDate = new Date(normalizedValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return String(value);
    }

    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZoneName: "short",
    }).format(parsedDate);
  };

  const fetchPresenceUsers = async () => {
    setIsLoading(true);
    setError("");
    setEmptyMessage("");

    const socketBaseUrl = (process.env.REACT_APP_SOCKET_WEB_URL || "").trim();
    if (!socketBaseUrl) {
      setError("REACT_APP_SOCKET_WEB_URL is missing in .env");
      setUsers([]);
      setIsLoading(false);
      return;
    }

    const presenceUrl = `${socketBaseUrl.replace(/\/$/, "")}/presence`;

    try {
      const response = await fetch(presenceUrl);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Presence endpoint not found (404)");
        }
        if (response.status === 500) {
          throw new Error("Presence server error (500)");
        }
        throw new Error(`Presence request failed (${response.status})`);
      }

      const data = await response.json();
      const mappedUsers = Array.isArray(data?.users) ? data.users : [];
      setUsers(mappedUsers);
      if (mappedUsers.length === 0) {
        setEmptyMessage("No live users found right now.");
      }
    } catch (err) {
      setError(err?.message || "Failed to fetch live users");
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPresenceUsers();
  }, []);

  return (
    <>
      <Breadcrumbs breadcrumbs={[{ name: "Live User", link: "" }]} />
      <div className="relative lg:mx-12 mx-3">
        <div className="flex justify-end my-2">
          <button
            onClick={fetchPresenceUsers}
            disabled={isLoading}
            className={`px-4 py-2 rounded-md text-white transition-all duration-200 ${
              isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-orange-500 hover:bg-orange-600"
            }`}
          >
            {isLoading ? "Refetching..." : "Refetch"}
          </button>
        </div>
        <div className="lg:h-[calc(100vh-210px)] overflow-auto my-2 hide-scroll">
          <table className="w-full text-sm text-left text-gray-500 border border-black">
            <thead className="text-sm text-orange-500 uppercase bg-gray-100 border border-black">
              <tr>
                <th scope="col" className="px-6 py-3 text-center">#</th>
                <th scope="col" className="px-6 py-3 text-center">Name</th>
                <th scope="col" className="px-6 py-3 text-center">Username</th>
                <th scope="col" className="px-6 py-3 text-center">Email</th>
                <th scope="col" className="px-6 py-3 text-center">Active</th>
                <th scope="col" className="px-6 py-3 text-center">Connected</th>
                <th scope="col" className="px-6 py-3 text-center">Last Login</th>
                <th scope="col" className="px-6 py-3 text-center">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user?.id || index} className="bg-white hover:bg-gray-50 border-b">
                  <td className="px-6 py-4 text-center">{index + 1}</td>
                  <td className="px-6 py-4 text-center">
                    {[user?.firstname, user?.lastname].filter(Boolean).join(" ") || "-"}
                  </td>
                  <td className="px-6 py-4 text-center">{user?.username || "-"}</td>
                  <td className="px-6 py-4 text-center">{user?.email || "-"}</td>
                  <td className="px-6 py-4 text-center">{user?.is_active ? "Yes" : "No"}</td>
                  <td className="px-6 py-4 text-center">{user?.is_connected ? "Yes" : "No"}</td>
                  <td className="px-6 py-4 text-center">{humanDateTime(user?.last_login)}</td>
                  <td className="px-6 py-4 text-center">{humanDateTime(user?.last_seen_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {isLoading && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <Loader w="10" h="10" color={"orange"} />
            </div>
          )}
          {!isLoading && !error && !emptyMessage && <Unavailable data={users} />}
          {!isLoading && emptyMessage && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-black opacity-60 text-lg">
              {emptyMessage}
            </div>
          )}
          {error && (
            <div className="mt-3 text-red-600 text-sm">
              {error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
